/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState } from "react";
import { matchPath } from "react-router";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { endOfDay } from "date-fns";

import api from "services/api";
import vouchers from "services/vouchers";

import { Access } from "common";
import { Breadcrumbs, DatePicker, Form, Loading, Page } from "components";
import { useDefaultDateFormat } from "helpers";

const breadcrumbs = [
  { name: "Home", to: "/" },
  {
    name: "Campanhas de Cashback Externo",
    to: "/external-cashback-campaigns/list",
  },
  { name: "Editar campanha", to: "" },
];

export function Edit(props) {
  const [campaign, setCampaign] = useState(null);
  const [planList, setPlanList] = useState([]);
  const [channelList, setChannelList] = useState([]);
  const [saving, setSaving] = useState(false);
  const [repproving, setRepproving] = useState(false);
  const [approving, setApproving] = useState(false);

  const handleGetID = () => {
    const match = matchPath(props.location.pathname, {
      path: "/external-cashback-campaigns/:id/edit",
    });

    return match.params.id;
  };

  const getPlans = () => {
    api
      .get("experience/v2/plans", {
        params: {
          page: 1,
          per: 99999,
        },
      })
      .then(({ data }) => {
        setPlanList(
          data.data.map(({ id, attributes }) => ({
            value: id,
            label: attributes.name,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getChannels = () => {
    vouchers
      .get("/channels", {
        params: {
          page: 1,
          per: 99999,
        },
      })
      .then(({ data }) => {
        setChannelList(
          data.data.map(({ id, attributes }) => ({
            value: id,
            label: attributes.name,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getExternalCashbackCampaign = () => {
    vouchers
      .get(`/experience/vouchers/${handleGetID()}`)
      .then(({ data: { data } }) => {
        setCampaign({
          ...data.attributes,
          channels: data.attributes.channels.map((channel) => ({
            value: channel.id,
            label: channel.name,
          })),
          plans: data.attributes.plans.map((plan) => ({
            value: plan.id,
            label: plan.name,
          })),
          all_plans: planList.length === data.attributes.plans.length,
          all_channels: channelList.length === data.attributes.channels.length,
          automatically_activated:
            data.attributes?.voucher_configuration?.automatically_activated,
        });
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível carregar a campanha, tente novamente!",
          icon: "error",
          confirmButtonText: "Ok",
          onClose: () => {
            props.history.push("/external-cashback-campaigns/list");
          },
        });
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);

    if (!campaign.title) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "Todos os campos precisam ser preenchidos",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if(campaign.cashback_value <= 0) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "O valor do cashback deve ser maior que 0",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (!campaign.startDate || !campaign.endDate) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "Precisa informar o início e o fim da campanha!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (new Date(campaign.startDate) > new Date(campaign.endDate)) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "A data de início deve ser menor que a de fim!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (
      !campaign.all_plans &&
      (!campaign.plans || campaign.plans.length === 0)
    ) {
      setSaving(false);
      return Swal.fire({
        title: "Informe os planos",
        text: "Você precisa informar os planos que deseja incluir nesta campanha!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (
      !campaign.all_channels &&
      (!campaign.channels || campaign.channels.length === 0)
    ) {
      setSaving(false);
      return Swal.fire({
        title: "Informe os canais",
        text: "Você precisa informar os canais que deseja incluir nesta campanha!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    const payload = {
      title: campaign.title,
      points: Number(campaign.value),
      cashback: Number(campaign.discount),
      start_at: useDefaultDateFormat(new Date(campaign.start_at)) ?? null,
      end_at: useDefaultDateFormat(endOfDay(new Date(campaign.end_at))) ?? null,
      active: campaign.active,
      app_only: campaign.app_only,
      activation_limit: Number(campaign.activation_limit),
      activated_only_once: campaign.invalidable,
      activated_automatically: campaign.activated_automatically,
      channel_ids: campaign.all_channels
        ? channelList.map(({ value }) => value)
        : campaign.channels.map(({ value }) => value),
      plan_ids: campaign.all_plans
        ? planList.map(({ value }) => value)
        : campaign.plans.map(({ value }) => value),
    };

    vouchers
      .put(`/external/campaigns/${handleGetID()}`, payload)
      .then(() => {
        Swal.fire({
          title: "Sucesso",
          text: "Campanha editada com sucesso!",
          icon: "success",
          confirmButtonText: "Ok",
          onClose: () => {
            props.history.push("/external-cashback-campaigns/list");
          },
        });
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível editar a campanha, tente novamente!",
          icon: "error",
          confirmButtonText: "Ok",
        });
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const handleRepprove = () => {
    setRepproving(true);

    Swal.fire({
      title: "Qual o motivo?",
      input: "text",
      inputAttributes: {
        autocapitalize: "off",
      },
      showCancelButton: true,
      confirmButtonText: "Reprovar!",
      cancelButtonText: "Cancelar",
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
    })
      .then((result) => {
        if (result.isConfirmed) {
          vouchers
            .post(`/experience/vouchers/${handleGetID()}/reprove`, {
              voucher_status_obs: result.value,
            })
            .then(() => {
              Swal.fire({
                title: "Reprovar Benefício",
                text: "Campanha reprovada com sucesso!",
                icon: "success",
                confirmButtonText: "Ok",
              });
            })
            .catch((error) => {
              Swal.fire({
                title: "Erro",
                text: "Não foi possível reprovar a campanha, tente novamente!",
                icon: "error",
                confirmButtonText: "Ok",
              });
            });
        }
      })
      .finally(() => {
        setRepproving(false);
      });
  };

  const handleApprove = () => {
    setApproving(true);

    vouchers
      .post(`/experience/vouchers/${handleGetID()}/approve`, {
        voucher_status_obs: "",
      })
      .then(() => {
        Swal.fire({
          title: "Sucesso",
          text: "Campanha aprovada com sucesso!",
          icon: "success",
          confirmButtonText: "Ok",
          onClose: () => {
            props.history.push("/external-cashback-campaigns/list");
          },
        });
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          title: "Erro",
          text: "Não foi possível aprovar a campanha, tente novamente!",
          icon: "error",
          confirmButtonText: "Ok",
        });
      })
      .finally(() => {
        setApproving(false);
      });
  };

  const handleChange = (field, value) => {
    setCampaign({
      ...campaign,
      [field]: value,
    });
  };

  useEffect(() => {
    getExternalCashbackCampaign();
    getPlans();
    getChannels();
  }, []);

  return (
    <>
      <Page>
        <Breadcrumbs breadcrumbs={breadcrumbs} />

        {campaign && (
          <Form className="page-form" onSubmit={handleSubmit}>
            <h3 className="form-title">Editar Campanha Externa</h3>

            {campaign.voucher_status === "reproved" && (
              <div className="alert alert-warning">
                <h4>Reprovado</h4>
                <i>{campaign.voucher_status_obs}</i>
              </div>
            )}

            <Form.Field>
              <Form.Label>Título</Form.Label>
              <Form.Input
                name="title"
                value={campaign.title}
                onChange={(e) => handleChange("title", e.target.value)}
                placeholder="Digite o título da campanha"
              />
            </Form.Field>

            <Form.Field>
              <Form.Label>Valor do Cashback</Form.Label>
              <Form.Input
                name="cashback_value"
                value={campaign.discount}
                type="number"
                onChange={(e) => handleChange("discount", e.target.value)}
                placeholder="Digite o valor do cashback"
              />
            </Form.Field>

            <Form.Field>
              <Form.Label>Limite de ativações</Form.Label>
              <Form.Input
                name="activation_limit"
                value={campaign.activation_limit}
                type="number"
                onChange={(e) =>
                  handleChange("activation_limit", e.target.value)
                }
                placeholder="Digite o limite de ativações"
              />
            </Form.Field>

            <Form.Field>
              <Form.Label>Pontos</Form.Label>
              <Form.Input
                name="points"
                value={campaign.value}
                type="number"
                onChange={(e) => handleChange("value", e.target.value)}
                placeholder="Digite os pontos"
              />
            </Form.Field>

            <Form.Field className="datepicker-field">
              <Form.Label>Início</Form.Label>
              <DatePicker
                locale="pt-BR"
                selected={new Date(campaign.start_at)}
                onChange={(date) => handleChange("start_at", date)}
                dateFormat="dd/MM/yyyy"
                minDate={new Date()}
              />
              <div className="icon-input">
                <span className="glyph glyph-calendar" />
              </div>
            </Form.Field>

            <Form.Field className="datepicker-field">
              <Form.Label>Fim</Form.Label>
              <DatePicker
                locale="pt-BR"
                selected={new Date(campaign.end_at)}
                onChange={(date) => handleChange("end_at", date)}
                minDate={new Date(campaign.start_at) ?? new Date()}
                disabled={campaign.start_at ? false : true}
                dateFormat="dd/MM/yyyy"
              />
              <div className="icon-input">
                <span className="glyph glyph-calendar" />
              </div>
            </Form.Field>

            <Form.Field className="field-select">
              <Form.Label>Canais</Form.Label>
              <Form.Select
                name="channels"
                value={campaign.channels}
                options={channelList}
                isMulti
                disabled={campaign.all_channels}
                clearable
                searchable
                closeOnSelect={false}
                placeholder={
                  campaign.all_channels
                    ? "Todos os canais"
                    : "Selecione os canais"
                }
                onChange={(value) => handleChange("channels", value)}
              />
            </Form.Field>

            <Form.Field className="checkbox-field">
              <Form.Label>Todos os canais</Form.Label>
              <Form.Checkbox
                checked={campaign.all_channels}
                name="all_channels"
                onChange={(event) => handleChange("all_channels", event)}
                label="Todos os canais"
              />
            </Form.Field>

            <Form.Field className="field-select">
              <Form.Label>Planos</Form.Label>
              <Form.Select
                name="plans"
                value={campaign.plans}
                options={planList}
                isMulti
                disabled={campaign.all_plans}
                clearable
                searchable
                closeOnSelect={false}
                placeholder={
                  campaign.all_plans ? "Todos os planos" : "Selecione os planos"
                }
                onChange={(value) => handleChange("plans", value)}
              />
            </Form.Field>

            <Form.Field className="checkbox-field">
              <Form.Label>Todos os planos</Form.Label>
              <Form.Checkbox
                checked={campaign.all_plans}
                name="all_plans"
                onChange={(event) => handleChange("all_plans", event)}
                label="Todos os planos"
              />
            </Form.Field>

            <hr />

            <Form.Field className="checkbox-field">
              <Form.Label>Ativado apenas uma vez</Form.Label>
              <Form.Checkbox
                name="activated_only_once"
                checked={campaign.invalidable}
                onChange={(event) => handleChange("invalidable", event)}
                label="Somente uma vez"
              />
            </Form.Field>

            <Form.Field className="checkbox-field">
              <Form.Label>Ativa automaticamente</Form.Label>
              <Form.Checkbox
                checked={campaign?.activated_automatically}
                name="activated_automatically"
                onChange={(event) =>
                  handleChange("activated_automatically", event)
                }
                label="Ativa automaticamente"
              />
            </Form.Field>

            <Form.Field className="checkbox-field">
              <Form.Label>Somente aplicativo</Form.Label>
              <Form.Checkbox
                checked={campaign.app_only}
                name="app_only"
                onChange={(event) => handleChange("app_only", event)}
                label="Somente aplicativo"
              />
            </Form.Field>

            <Form.Field className="checkbox-field">
              <Form.Label>Ativo</Form.Label>
              <Form.Checkbox
                checked={campaign.active}
                name="active"
                onChange={(event) => handleChange("active", event)}
                label="Ativo"
              />
            </Form.Field>

            <div className="buttons">
              <Link
                to={{ pathname: "/external-cashback-campaigns/list" }}
                className="button-cancel"
              >
                Cancelar
              </Link>

              {Access.hasPermissions("approve-benefits") &&
                campaign.voucher_status !== "reproved" && (
                  <Form.Button
                    type="button"
                    className="button-cancel"
                    onClick={handleRepprove}
                    disabled={saving}
                  >
                    {repproving ? (
                      <Loading type="btn" size="small" text="Reprovando..." />
                    ) : (
                      "Reprovar"
                    )}
                  </Form.Button>
                )}

              {Access.hasPermissions("approve-benefits") &&
                campaign.voucher_status !== "approve" && (
                  <Form.Button
                    type="button"
                    className="button-save margin-right-15"
                    onClick={handleApprove}
                    disabled={saving}
                  >
                    {approving ? (
                      <Loading type="btn" size="small" text="Aprovando..." />
                    ) : (
                      "Aprovar"
                    )}
                  </Form.Button>
                )}

              {campaign.voucher_status === "pending" ? (
                <Form.Button
                  className="button-save margin-right-15"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <Loading type="btn" size="small" text="Salvando..." />
                  ) : (
                    "Salvar"
                  )}
                </Form.Button>
              ) : null}
            </div>
          </Form>
        )}
      </Page>
    </>
  );
}
